import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import "./AlertFormEdit.css";
import { useIntl } from "react-intl";
import { Toast } from "../../../helpers/alert";
import useFilteredUsersByDevice from "../../../hooks/useFilteredUsersByDevice";

const notifyOptions = [
  { value: "1h", label: "1 hora" },
  { value: "3h", label: "3 horas" },
  { value: "12h", label: "12 horas" },
  { value: "24h", label: "24 horas" },
];

const TunnelAlertFormEdit = ({
  devices,
  users,
  onCancel,
  onSave,
  initialData,
  userPrivilege,
}) => {
  const intl = useIntl();
  const ToastInstance =
    Toast ||
    Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });

  // ---------- Normalizadores (IDs o strings → {value,label}) ----------
  const toEmailOptions = useCallback(
    (arr = []) =>
      arr
        .map((val) => {
          const str = String(val ?? "");
          const isNumeric = typeof val === "number" || /^[0-9]+$/.test(str);
          if (isNumeric) {
            const u = users?.find((x) => x.id === Number(val));
            return u?.email ? { value: u.id, label: u.email } : null;
          }
          const u = users?.find((x) => x.email === str);
          return u
            ? { value: u.id, label: u.email }
            : str
            ? { value: str, label: str }
            : null; // fallback
        })
        .filter(Boolean),
    [users]
  );

  const toPhoneOptions = useCallback(
    (arr = []) =>
      arr
        .map((val) => {
          const str = String(val ?? "");
          const isNumeric = typeof val === "number" || /^[0-9]+$/.test(str);
          if (isNumeric) {
            const u = users?.find((x) => x.id === Number(val));
            return u?.phone ? { value: u.id, label: String(u.phone) } : null;
          }
          const u = users?.find((x) => String(x.phone) === str);
          return u
            ? { value: u.id, label: String(u.phone) }
            : str
            ? { value: str, label: str }
            : null; // fallback
        })
        .filter(Boolean),
    [users]
  );

  const toDeviceOptions = useCallback(
    (arr = []) => {
      const byId = new Map((devices || []).map((d) => [d.value, d]));
      return arr.map((devId) => {
        const id = devId?.device_id ?? devId?.id ?? devId;
        return (
          byId.get(id) || {
            value: id,
            label: devId?.device?.name
              ? `${devId.device.name} (${devId.device.serial_number})`
              : String(id),
          }
        );
      });
    },
    [devices]
  );

  const [formData, setFormData] = useState(() => {
    const emailsFromInitial =
      initialData?.config?.frequency?.realtime?.emails || [];
    const phonesFromInitial =
      initialData?.config?.frequency?.realtime?.phones || [];
    return {
      name: initialData?.name || "",
      active: initialData?.active === 1 || initialData?.active === true,
      parameter: initialData?.config?.parameter
        ? {
            label: initialData.config.parameter,
            value: initialData.config.parameter,
          }
        : null,
      frequency:
        initialData?.config?.frequencyType ||
        initialData?.frequency ||
        "hourly",
      weeks: Number(initialData?.config?.weeks ?? 4),
      devices: toDeviceOptions(initialData?.config?.devices || []),
      grouping: initialData?.config?.grouping || "media",
      upperMargin: Number(initialData?.config?.upperMargin ?? 5),
      lowerMargin: Number(initialData?.config?.lowerMargin ?? 5),
      emails: toEmailOptions(emailsFromInitial),
      phones: toPhoneOptions(phonesFromInitial),
      notifyEvery:
        typeof initialData?.config?.frequency?.realtime?.rateLimit === "string"
          ? initialData.config.frequency.realtime.rateLimit
          : "1h",
    };
  });

  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);

  // ---------- Rehidratación segura ----------
  const lastInitKeyRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);

  const initKey = useMemo(
    () =>
      initialData?.id ??
      initialData?.uuid ??
      initialData?.name ??
      JSON.stringify({
        parameter: initialData?.config?.parameter,
        frequencyType: initialData?.config?.frequencyType,
        weeks: initialData?.config?.weeks,
        devices: (initialData?.config?.devices || []).map(
          (d) => d?.device_id ?? d?.id ?? d
        ),
        grouping: initialData?.config?.grouping,
        upperMargin: initialData?.config?.upperMargin,
        lowerMargin: initialData?.config?.lowerMargin,
        rateLimit: initialData?.config?.frequency?.realtime?.rateLimit,
        emails: initialData?.config?.frequency?.realtime?.emails,
        phones: initialData?.config?.frequency?.realtime?.phones,
        active: initialData?.active,
        name: initialData?.name,
      }),
    [initialData]
  );

  const buildStateFromInitial = useCallback(() => {
    if (!initialData) return null;
    const emailsFromInitial =
      initialData?.config?.frequency?.realtime?.emails || [];
    const phonesFromInitial =
      initialData?.config?.frequency?.realtime?.phones || [];
    return {
      name: initialData?.name || "",
      active: initialData?.active === 1 || initialData?.active === true,
      parameter: initialData?.config?.parameter
        ? {
            label: initialData.config.parameter,
            value: initialData.config.parameter,
          }
        : null,
      frequency:
        initialData?.config?.frequencyType ||
        initialData?.frequency ||
        "hourly",
      weeks: Number(initialData?.config?.weeks ?? 4),
      devices: toDeviceOptions(initialData?.config?.devices || []),
      grouping: initialData?.config?.grouping || "media",
      upperMargin: Number(initialData?.config?.upperMargin ?? 5),
      lowerMargin: Number(initialData?.config?.lowerMargin ?? 5),
      emails: toEmailOptions(emailsFromInitial),
      phones: toPhoneOptions(phonesFromInitial),
      notifyEvery:
        typeof initialData?.config?.frequency?.realtime?.rateLimit === "string"
          ? initialData.config.frequency.realtime.rateLimit
          : "1h",
    };
  }, [initialData, toDeviceOptions, toEmailOptions, toPhoneOptions]);

  useEffect(() => {
    if (
      !initialData ||
      !(Array.isArray(users) && users.length) ||
      !(Array.isArray(devices) && devices.length)
    ) {
      return;
    }
    const sameInit = lastInitKeyRef.current === initKey;
    if (isDirty && sameInit) return;

    const next = buildStateFromInitial();
    if (!next) return;

    setFormData((prev) => {
      const simplify = (s) => ({
        name: s.name,
        active: !!s.active,
        parameter: s.parameter?.label || "",
        frequency: s.frequency,
        weeks: Number(s.weeks),
        deviceIds: (s.devices || []).map((d) => d.value).sort(),
        grouping: s.grouping,
        upperMargin: Number(s.upperMargin),
        lowerMargin: Number(s.lowerMargin),
        notifyEvery: s.notifyEvery,
        emailVals: (s.emails || []).map((e) => e.value).sort(),
        phoneVals: (s.phones || []).map((p) => p.value).sort(),
      });
      return JSON.stringify(simplify(prev)) === JSON.stringify(simplify(next))
        ? prev
        : next;
    });

    lastInitKeyRef.current = initKey;
    setIsDirty(false);
  }, [initKey, initialData, users, devices, buildStateFromInitial, isDirty]);

  // ---------- Parámetros disponibles (intersección por label) ----------
  const extractVisibleParams = (device) => {
    const params = [];
    if (Array.isArray(device?.data?.basic_parameters)) {
      device.data.basic_parameters.forEach((param) => {
        if (param.tag && param.visible === 1) {
          params.push({
            value: `basic-${param.idbasic_parameters}`,
            label: param.tag,
            type: "basic",
            id: param.idbasic_parameters,
            unit: param?.units || "",
          });
        }
      });
    }
    if (Array.isArray(device?.data?.calculated_parameters)) {
      device.data.calculated_parameters.forEach((param) => {
        if (param.tag && param.device_calculated_parameter?.visible === 1) {
          params.push({
            value: `calc-${param.idcalculated_parameter}`,
            label: param.tag,
            type: "calculated",
            id: param.idcalculated_parameter,
            unit: param?.units || "",
          });
        }
      });
    }
    return params;
  };

  const availableParams = useMemo(() => {
    if (!formData.devices?.length) return [];
    const selectedFullDevices = formData.devices
      .map((sel) => devices.find((d) => d.value === sel.value))
      .filter(Boolean);
    if (!selectedFullDevices.length) return [];
    const perDevice = selectedFullDevices.map(extractVisibleParams);
    return perDevice.reduce((acc, curr) =>
      acc.filter((a) => curr.some((b) => b.label === a.label))
    );
  }, [formData.devices, devices]);

  // Si el parámetro seleccionado deja de existir, limpiarlo
  useEffect(() => {
    if (!formData.parameter) return;
    const ok = availableParams.some(
      (p) => p.label === formData.parameter.label
    );
    if (!ok) setFormData((prev) => ({ ...prev, parameter: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableParams]);

  // ---------- Filtrado de usuarios por dispositivo/privilegios ----------
  const filteredUsers = useFilteredUsersByDevice(
    formData.devices,
    users,
    userPrivilege
  );

  // ---------- Opciones de selects de usuarios (derivadas de filteredUsers) ----------
  const emailOptions = useMemo(
    () =>
      (filteredUsers || [])
        .filter((u) => !!u.email)
        .map((u) => ({ value: u.id, label: u.email })),
    [filteredUsers]
  );
  const phoneOptions = useMemo(
    () =>
      (filteredUsers || [])
        .filter((u) => !!u.phone)
        .map((u) => ({ value: u.id, label: String(u.phone) })),
    [filteredUsers]
  );

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData.name || !formData.name.trim()) {
      newErrors.name =
        intl?.formatMessage({
          id: "error.name",
          defaultMessage: "El nombre es obligatorio",
        }) || "El nombre es obligatorio";
    }
    if (!formData.devices?.length) {
      newErrors.devices =
        intl?.formatMessage({
          id: "error.devices",
          defaultMessage: "Debe seleccionar al menos un dispositivo",
        }) || "Debe seleccionar al menos un dispositivo";
    }
    if (!formData.parameter) {
      newErrors.parameter =
        intl?.formatMessage({
          id: "error.parameter",
          defaultMessage: "Debe seleccionar un parámetro",
        }) || "Debe seleccionar un parámetro";
    }
    if (!formData.weeks || Number(formData.weeks) < 1) {
      newErrors.weeks =
        intl?.formatMessage({
          id: "error.weeks",
          defaultMessage: "Semanas debe ser >= 1",
        }) || "Semanas debe ser >= 1";
    }
    if (formData.upperMargin === "" || Number(formData.upperMargin) < 0) {
      newErrors.upperMargin =
        intl?.formatMessage({
          id: "error.upperMargin",
          defaultMessage: "El margen superior no puede ser negativo",
        }) || "El margen superior no puede ser negativo";
    }
    if (formData.lowerMargin === "" || Number(formData.lowerMargin) < 0) {
      newErrors.lowerMargin =
        intl?.formatMessage({
          id: "error.lowerMargin",
          defaultMessage: "El margen inferior no puede ser negativo",
        }) || "El margen inferior no puede ser negativo";
    }
    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
    return newErrors;
  }, [formData, intl]);

  useEffect(() => {
    validateForm();
  }, [formData, validateForm]);

  const handleChange = (field, value) => {
    setIsDirty(true);
    if (field === "notifyEvery") {
      const normalized =
        typeof value === "object" && value !== null ? value.value : value;
      setFormData((prev) => ({ ...prev, notifyEvery: normalized }));
      return;
    }
    if (field === "weeks")
      return setFormData((prev) => ({ ...prev, weeks: Number(value) }));
    if (field === "upperMargin")
      return setFormData((prev) => ({ ...prev, upperMargin: Number(value) }));
    if (field === "lowerMargin")
      return setFormData((prev) => ({ ...prev, lowerMargin: Number(value) }));

    if (field === "devices") {
      // Al cambiar dispositivos, vaciar destinatarios (emails/phones) y, si el parámetro deja de aplicar, ya hay efecto que lo limpia.
      setFormData((prev) => ({
        ...prev,
        devices: value,
        emails: [],
        phones: [],
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const validationErrors = validateForm();

    Swal.fire({
      title: "¿Desea guardar los cambios de la alerta?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText:
        intl?.formatMessage({
          id: "confirm.yes",
          defaultMessage: "Sí, continuar.",
        }) || "Sí, continuar.",
      cancelButtonText:
        intl?.formatMessage({ id: "confirm.no", defaultMessage: "Cancelar" }) ||
        "Cancelar",
    }).then((result) => {
      if (result.isConfirmed && Object.keys(validationErrors).length === 0) {
        const payload = {
          type: "TUNEL",
          name: formData.name?.toUpperCase(),
          active: formData.active ? 1 : 0,
          config: {
            parameter: formData.parameter?.label,
            frequencyType: formData.frequency,
            weeks: Number(formData.weeks),
            devices: formData.devices.map((d) => d.value),
            grouping: formData.grouping,
            upperMargin: Number(formData.upperMargin),
            lowerMargin: Number(formData.lowerMargin),
            frequency: {
              realtime: {
                enabled:
                  (formData.emails?.length || 0) > 0 ||
                  (formData.phones?.length || 0) > 0,
                rateLimit: formData.notifyEvery,
                emails: formData.emails.map((e) => e.value),
                phones: formData.phones.map((p) => p.value),
              },
              daily: { enabled: false, time: null, emails: [], phones: [] },
              weekly: {
                enabled: false,
                day: null,
                time: null,
                emails: [],
                phones: [],
              },
              monthly: {
                enabled: false,
                day: null,
                time: null,
                emails: [],
                phones: [],
              },
            },
          },
        };
        onSave(payload);
      } else if (Object.keys(validationErrors).length) {
        ToastInstance.fire({
          icon: "info",
          title:
            intl?.formatMessage(
              {
                id: "error.saveTunel",
                defaultMessage: "No se pudo guardar la alerta: {errors}",
              },
              { errors: Object.values(validationErrors).join(", ") }
            ) ||
            `No se pudo guardar la alerta: ${Object.values(
              validationErrors
            ).join(", ")}`,
        });
      }
    });
  };

  return (
    <div className="tunel-form">
      <h3 className="tunel-title">Editar alerta: Alerta de túnel</h3>

      <label className="tunel-label">Nombre de la alerta</label>
      <input
        type="text"
        className="tunel-input"
        value={formData.name}
        onChange={(e) => handleChange("name", e.target.value)}
        placeholder="Ej: Túnel consumo energía"
      />
      {errors.name && <small className="error-text">{errors.name}</small>}

      <label className="tunel-label">¿Activar?</label>
      <div className="tunel-inline">
        <label>
          <input
            type="radio"
            checked={!!formData.active}
            onChange={() => handleChange("active", true)}
          />{" "}
          Sí
        </label>
        <label>
          <input
            type="radio"
            checked={!formData.active}
            onChange={() => handleChange("active", false)}
          />{" "}
          No
        </label>
      </div>

      <label className="tunel-label">Dispositivos</label>
      <Select
        isMulti
        className="tunel-select"
        options={devices}
        value={formData.devices}
        onChange={(val) => handleChange("devices", val)}
        placeholder="Selecciona uno o más dispositivos"
      />
      {errors.devices && <small className="error-text">{errors.devices}</small>}

      <div className="tunel-inline">
        <div className="tunel-inline-block">
          <label className="tunel-label">Parámetro</label>
          <Select
            className="tunel-select"
            options={useMemo(() => availableParams, [availableParams])}
            value={formData.parameter}
            onChange={(val) => handleChange("parameter", val)}
            placeholder="Selecciona parámetro"
            isDisabled={!formData.devices.length}
          />
          {errors.parameter && (
            <small className="error-text">{errors.parameter}</small>
          )}
        </div>

        <div className="tunel-inline-block">
          <label className="tunel-label">Frecuencia</label>
          <Select
            className="tunel-select"
            value={[
              { value: "hourly", label: "Horaria" },
              { value: "diary", label: "Diaria" },
              { value: "weekly", label: "Semanal" },
              { value: "monthly", label: "Mensual" },
            ].find((o) => o.value === formData.frequency)}
            options={[
              { value: "hourly", label: "Horaria" },
              { value: "diary", label: "Diaria" },
              { value: "weekly", label: "Semanal" },
              { value: "monthly", label: "Mensual" },
            ]}
            onChange={(val) => handleChange("frequency", val.value)}
          />
        </div>

        <div className="tunel-inline-block">
          <label className="tunel-label">Semanas</label>
          <input
            type="number"
            className="tunel-input small"
            value={formData.weeks}
            onChange={(e) => handleChange("weeks", e.target.value)}
            min={1}
          />
          {errors.weeks && <small className="error-text">{errors.weeks}</small>}
        </div>
      </div>

      <label className="tunel-label">Agrupación</label>
      <Select
        className="tunel-select"
        value={{ value: formData.grouping, label: "Media" }}
        options={[{ value: "media", label: "Media" }]}
        onChange={(val) => handleChange("grouping", val.value)}
      />

      <label className="tunel-label">Margen superior (%)</label>
      <input
        type="number"
        className="tunel-input small"
        value={formData.upperMargin}
        onChange={(e) => handleChange("upperMargin", e.target.value)}
        min={0}
      />
      {errors.upperMargin && (
        <small className="error-text">{errors.upperMargin}</small>
      )}

      <label className="tunel-label">Margen inferior (%)</label>
      <input
        type="number"
        className="tunel-input small"
        value={formData.lowerMargin}
        onChange={(e) => handleChange("lowerMargin", e.target.value)}
        min={0}
      />
      {errors.lowerMargin && (
        <small className="error-text">{errors.lowerMargin}</small>
      )}

      <h4 className="tunel-subtitle">Avisos</h4>
      <div className="tunel-inline">
        Avísame máximo 1 vez cada
        <Select
          className="tunel-select small"
          value={
            notifyOptions.find((opt) => opt.value === formData.notifyEvery) ||
            notifyOptions[0]
          }
          options={notifyOptions}
          onChange={(val) => handleChange("notifyEvery", val)}
        />
      </div>

      <div className="tunel-inline">
        <label>
          <input
            type="checkbox"
            checked={formData.emails.length > 0}
            readOnly
          />{" "}
          Enviar aviso por correo electrónico
        </label>
        <Select
          isMulti
          className="tunel-select"
          options={emailOptions}
          value={formData.emails}
          onChange={(val) => handleChange("emails", val)}
        />
      </div>

      {/* <div className="tunel-inline">
        <label>
          <input
            type="checkbox"
            checked={formData.phones.length > 0}
            readOnly
          />{" "}
          Enviar aviso por SMS
        </label>
        <Select
          isMulti
          className="tunel-select"
          options={phoneOptions}
          value={formData.phones}
          onChange={(val) => handleChange("phones", val)}
        />
      </div> */}

      <div className="tunel-actions">
        <button
          className="tunel-btn-save"
          onClick={handleSubmit}
          disabled={!isValid}
        >
          Guardar
        </button>
        <button className="tunel-btn-cancel" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default TunnelAlertFormEdit;
